import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout.tsx';
import { SignalTimeline } from '../components/SignalTimeline.tsx';
import { ReportPanel } from '../components/report/ReportPanel.tsx';
import { ApiError, request } from '../lib/api-client.ts';
import { formatJst } from '../lib/format.ts';
import type { AuthUser } from '../types/auth.ts';
import type { IndustryDetail } from '../types/industry.ts';

type Props = {
  user: AuthUser;
  onLogout: () => void;
};

type Status =
  | { kind: 'loading' }
  | { kind: 'ready'; data: IndustryDetail }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string };

/**
 * 業種詳細（縦断モード）。
 * 仕様: knowledge/products/trendscope/specs/ui-layout.md §2
 */
export const IndustryDetailPage = ({ user, onLogout }: Props): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    if (!id) {
      setStatus({ kind: 'not_found' });
      return () => {
        mountedRef.current = false;
      };
    }
    const controller = new AbortController();
    request<IndustryDetail>(`/api/industry/${id}/detail`, { signal: controller.signal })
      .then((data) => {
        if (mountedRef.current) setStatus({ kind: 'ready', data });
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        if (err instanceof ApiError && err.status === 404) {
          setStatus({ kind: 'not_found' });
          return;
        }
        const message = err instanceof Error ? err.message : '取得に失敗しました';
        setStatus({ kind: 'error', message });
      });
    return () => {
      mountedRef.current = false;
      controller.abort();
    };
  }, [id]);

  return (
    <DashboardLayout user={user} onLogout={onLogout}>
      <Link to="/" style={backStyle}>← ダッシュボードに戻る</Link>
      {renderContent(status)}
    </DashboardLayout>
  );
};

const renderContent = (status: Status): React.JSX.Element => {
  if (status.kind === 'loading') return <p>読み込み中…</p>;
  if (status.kind === 'not_found') return <p>業種が見つかりません。</p>;
  if (status.kind === 'error') return <p style={errorStyle}>{status.message}</p>;

  return (
    <>
      <h2 style={titleStyle}>{status.data.industry.name}</h2>
      <dl style={metaStyle}>
        <dt>code</dt><dd>{status.data.industry.code}</dd>
        <dt>JSIC</dt><dd>{status.data.industry.jsic_code ?? '—'}</dd>
        <dt>更新</dt><dd>{formatJst(status.data.data_updated_at)}</dd>
        <dt>algo_version</dt><dd>{status.data.algo_version}</dd>
      </dl>
      <SignalTimeline signals={status.data.signals} />
      <ReportPanel
        industryId={status.data.industry.id}
        reportType="analysis"
        title="📊 なぜ伸びているか分析"
      />
      <ReportPanel
        industryId={status.data.industry.id}
        reportType="ideas"
        title="💡 参入アイデア + YouTubeネタ"
      />
    </>
  );
};

const titleStyle: React.CSSProperties = { marginTop: 16 };
const backStyle: React.CSSProperties = { fontSize: 13, color: '#1976d2', textDecoration: 'none' };
const errorStyle: React.CSSProperties = { color: '#c0392b' };
const metaStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '120px 1fr',
  rowGap: 4,
  columnGap: 12,
  margin: '12px 0 24px',
  fontSize: 13,
};
